<template>
  <article class="self-service-card wide-card conference-list-card">
    <div class="conf-list-header">
      <div>
        <h3>学术会议</h3>
        <p class="conf-list-subtitle">{{ listHint }}</p>
      </div>
      <div class="conf-branch-filter" v-click-outside="closeBranchMenu">
        <span class="conf-branch-filter__label">筛选分会</span>
        <button
          ref="branchTrigger"
          type="button"
          class="conf-branch-filter__trigger"
          :aria-expanded="branchMenuOpen ? 'true' : 'false'"
          @click="toggleBranchMenu"
        >
          <span class="conf-branch-filter__value">{{ selectedFilterLabel }}</span>
          <span class="conf-branch-filter__caret" aria-hidden="true" />
        </button>
        <ul
          v-show="branchMenuOpen"
          ref="branchMenu"
          class="conf-branch-filter__menu"
          :class="{ 'is-up': branchMenuUp }"
          :style="branchMenuStyle"
          role="listbox"
        >
          <li>
            <button
              type="button"
              class="conf-branch-filter__option"
              :class="{ 'is-active': !front.activeServiceBranchId }"
              role="option"
              @click="selectBranchFilter('')"
            >
              全部已绑定分会
            </button>
          </li>
          <li v-for="item in filterBranchOptions" :key="item.associationId">
            <button
              type="button"
              class="conf-branch-filter__option"
              :class="{ 'is-active': String(front.activeServiceBranchId) === String(item.associationId) }"
              role="option"
              @click="selectBranchFilter(String(item.associationId))"
            >
              {{ item.label }}
            </button>
          </li>
        </ul>
      </div>
    </div>

    <div v-if="front.conferenceFeedback" class="service-notice warning" role="alert">
      <strong>无法继续报名</strong>
      <span>{{ front.conferenceFeedback }}</span>
    </div>

    <div v-if="!front.filteredPublicConferences.length" class="conf-list-empty">
      <strong>暂无可查看的会议</strong>
      <p>{{ emptyHint }}</p>
    </div>

    <div v-else class="conf-list">
      <article
        v-for="conference in front.filteredPublicConferences"
        :key="conference.conferenceId"
        class="conf-list-item"
      >
        <div class="conf-list-item__main">
          <div class="conf-list-item__tags">
            <span
              class="conf-tag"
              :class="isRegistrationOpen(conference) ? 'conf-tag--open' : 'conf-tag--closed'"
            >
              {{ statusLabel(conference) }}
            </span>
            <span class="conf-tag conf-tag--branch" :class="{ 'is-main': isMainSociety(conference) }">
              {{ isMainSociety(conference) ? '★ ' : '' }}{{ branchName(conference) }}
            </span>
            <span
              v-if="registrationOf(conference)"
              class="conf-tag"
              :class="regTagClass(registrationOf(conference))"
            >
              {{ front.registrationStatusLabel(registrationOf(conference)) }}
            </span>
          </div>

          <h4 class="conf-list-item__title" @click="front.openConferenceNotice(conference)">
            {{ conference.conferenceTitle }}
          </h4>
          <p class="conf-list-item__desc">{{ conferenceDesc(conference) }}</p>

          <div class="conf-list-item__meta">
            <span>{{ front.formatDateRange(conference.startDate, conference.endDate) }}</span>
            <span>{{ conference.city || '地点待定' }}</span>
            <span>{{ feeLabel(conference) }}</span>
            <span v-if="conference.paymentDeadline">缴费截止 {{ front.formatDate(conference.paymentDeadline) }}</span>
          </div>
        </div>

        <div class="conf-list-item__actions">
          <button type="button" class="primary-action compact" @click="front.openConferenceNotice(conference)">
            查看会议通知
          </button>
          <button type="button" class="conf-link-action" @click="front.openConferenceDetailView(conference)">
            报名与详情 ›
          </button>

          <template v-if="!registrationOf(conference)">
            <button
              v-if="isRegistrationOpen(conference) && front.needsMembershipChoice"
              type="button"
              class="primary-action compact"
              @click="front.goServicesTab('member')"
            >
              先选择参与方式
            </button>
            <button
              v-else-if="isRegistrationOpen(conference)"
              type="button"
              class="gold-action compact"
              @click="front.startConferencePayment(conference)"
            >
              缴纳注册费
            </button>
            <span v-else class="conf-status-text is-closed">
              {{ statusLabel(conference) }}
            </span>
          </template>

          <template v-else>
            <!-- 冻结态：引导续费 -->
            <template v-if="registrationOf(conference).frozenDueToExpiry === '1' && !isOfficialCardOnsite(registrationOf(conference))">
              <span class="conf-status-text is-frozen">会员到期冻结</span>
              <button
                type="button"
                class="gold-action compact"
                @click="front.goServicesTab('member')"
              >
                立即续费
              </button>
            </template>

            <!-- 正常态 -->
            <template v-else>
              <button
                v-if="canResubmitVoucher(registrationOf(conference))"
                type="button"
                class="gold-action compact"
                @click="front.openConferencePaymentFlow(conference)"
              >
                重新提交凭证
              </button>

              <button
                v-if="canViewPaymentProgress(registrationOf(conference))"
                type="button"
                class="blue-action compact"
                @click="front.openConferencePaymentFlow(conference)"
              >
                查看缴费进度
              </button>

              <button
                v-if="front.registrationCanFillAttendee(registrationOf(conference))"
                type="button"
                class="green-action compact"
                @click="front.openAttendeeForm(registrationOf(conference))"
              >
                填写参会信息
              </button>

              <button
                v-if="canUploadInvoice(registrationOf(conference))"
                type="button"
                class="blue-action compact"
                @click="front.openConferencePaymentFlow(conference)"
              >
                上传发票
              </button>
            </template>
          </template>
        </div>
      </article>
    </div>
  </article>
</template>

<script>
import { displayAssociationName } from '../../data/branchSite'

export default {
  name: 'ConferenceListCard',
  inject: ['front'],
  directives: {
    clickOutside: {
      bind(el, binding) {
        el.__confClickOutsideHandler = (event) => {
          if (!el.contains(event.target)) {
            binding.value(event)
          }
        }
        document.addEventListener('click', el.__confClickOutsideHandler, true)
      },
      unbind(el) {
        document.removeEventListener('click', el.__confClickOutsideHandler, true)
        delete el.__confClickOutsideHandler
      }
    }
  },
  data() {
    return {
      branchMenuOpen: false,
      branchMenuUp: false,
      branchMenuMaxHeight: 360
    }
  },
  computed: {
    branchMenuStyle() {
      return {
        maxHeight: `${this.branchMenuMaxHeight}px`
      }
    },
    /** 下拉选项：总会 + 已绑定分会（可手动筛选；默认「全部已绑定」） */
    filterBranchOptions() {
      const options = []
      const main = this.front.mainSocietyAssociation
      options.push({
        associationId: (main && main.associationId) || 1,
        label: displayAssociationName(main) || '中国古生物学会'
      })
      const boundBranches = (this.front.branchAssociations || []).filter(item => this.front.isAssociationBound(item))
      boundBranches.forEach(item => {
        options.push({
          associationId: item.associationId,
          label: displayAssociationName(item)
        })
      })
      // 深链落到未绑定分会时，仍保留当前选项，避免下拉空白
      const currentId = this.front.activeServiceBranchId
      if (currentId && !options.some(item => String(item.associationId) === String(currentId))) {
        options.push({
          associationId: currentId,
          label: `${displayAssociationName(this.front.activeServiceBranchName) || '未知分会'}（未绑定）`
        })
      }
      return options
    },
    selectedFilterLabel() {
      if (!this.front.activeServiceBranchId) {
        return '全部已绑定分会'
      }
      const match = this.filterBranchOptions.find(
        item => String(item.associationId) === String(this.front.activeServiceBranchId)
      )
      return (match && match.label) || this.front.activeServiceBranchName || '已选分会'
    },
    listHint() {
      if (this.front.activeServiceBranchName) {
        return `当前筛选「${displayAssociationName(this.front.activeServiceBranchName) || this.front.activeServiceBranchName}」；可切换下拉查看其他已绑定分会或全部会议。`
      }
      const bound = this.front.boundBranchCount || 0
      if (bound) {
        return `默认展示全部已绑定分会与总学会会议；可用右侧下拉筛选单个分会。已绑定 ${bound} 个分会。`
      }
      return '默认仅展示总学会会议；绑定分会后可在下拉中筛选查看对应会议。'
    },
    emptyHint() {
      if (this.front.activeServiceBranchName) {
        return `「${this.front.activeServiceBranchName}」暂无开放会议。可切换下拉查看其他已绑定分会，或选择「全部已绑定分会」。`
      }
      if (!(this.front.boundBranchCount > 0)) {
        return '请先在「会议绑定管理」绑定分会后查看对应会议；总学会会议无需绑定。'
      }
      return '当前已绑定分会暂无开放报名的学术会议，请稍后再来查看。'
    }
  },
  methods: {
    displayAssociationName,
    closeBranchMenu() {
      this.branchMenuOpen = false
      this.branchMenuUp = false
    },
    toggleBranchMenu() {
      if (this.branchMenuOpen) {
        this.closeBranchMenu()
        return
      }
      this.branchMenuOpen = true
      this.$nextTick(() => {
        this.layoutBranchMenu()
        this.scrollActiveBranchIntoView()
      })
    },
    /**
     * 空列表时页面较矮，绝对定位菜单易被 main{overflow:hidden} 裁切，
     * 导致滚到尽头也看不到最后几项。按 main/视口剩余空间限制高度，不够则向上展开。
     */
    layoutBranchMenu() {
      const trigger = this.$refs.branchTrigger
      if (!trigger || typeof trigger.getBoundingClientRect !== 'function') return
      const rect = trigger.getBoundingClientRect()
      const gap = 8
      const mainEl = typeof document !== 'undefined' ? document.querySelector('main') : null
      const mainRect = mainEl && mainEl.getBoundingClientRect ? mainEl.getBoundingClientRect() : null
      const clipBottom = mainRect ? Math.min(mainRect.bottom, window.innerHeight) : window.innerHeight
      const clipTop = mainRect ? Math.max(mainRect.top, 0) : 0
      const spaceBelow = Math.max(0, clipBottom - rect.bottom - gap)
      const spaceAbove = Math.max(0, rect.top - clipTop - gap)
      const preferUp = spaceBelow < 220 && spaceAbove > spaceBelow
      this.branchMenuUp = preferUp
      const available = preferUp ? spaceAbove : spaceBelow
      this.branchMenuMaxHeight = Math.max(160, Math.min(360, Math.floor(available)))
    },
    scrollActiveBranchIntoView() {
      const menu = this.$refs.branchMenu
      if (!menu) return
      const active = menu.querySelector('.conf-branch-filter__option.is-active')
      if (!active || typeof active.scrollIntoView !== 'function') return
      active.scrollIntoView({ block: 'nearest' })
    },
    selectBranchFilter(branch) {
      this.closeBranchMenu()
      this.front.goServicesTab('conference', { branch, keepView: true })
    },
    registrationOf(conference) {
      return this.front.registrationByConferenceId(conference.conferenceId)
    },
    isOfficialCardOnsite(registration) {
      return Boolean(registration && registration.paymentMethod === 'OFFICIAL_CARD_ONSITE')
    },
    branchName(conference) {
      return displayAssociationName(this.front.associationNameById(conference.associationId))
        || this.front.associationNameById(conference.associationId)
    },
    isMainSociety(conference) {
      return Number(conference.associationId) === 1
    },
    isRegistrationOpen(conference) {
      return this.front.isConferenceRegistrationOpen(conference)
    },
    statusLabel(conference) {
      return this.front.conferenceListStatusLabel(conference)
    },
    conferenceDesc(conference) {
      if (conference.description || conference.intro) {
        return conference.description || conference.intro
      }
      return '凭证审核通过后可先填写参会基础信息、摘要与住宿；野外与盖章材料需发票终审通过后开放。'
    },
    feeLabel(conference) {
      const amount = this.front.conferenceUserFeeAmount(conference)
      if (amount != null && amount !== '') {
        return `注册费 ¥${amount}`
      }
      return '注册费按身份匹配'
    },
    regTagClass(registration) {
      if (registration && registration.frozenDueToExpiry === '1' && !this.isOfficialCardOnsite(registration)) return 'conf-tag--frozen'
      const status = registration && registration.paymentStatus
      if (status === 'CONFIRMED') return 'conf-tag--ok'
      if (['VOUCHER_REJECTED', 'INVOICE_REJECTED'].includes(status)) return 'conf-tag--danger'
      if (['VOUCHER_REVIEW', 'INVOICE_REVIEW', 'INVOICE_PENDING', 'INVOICE_OVERDUE'].includes(status)) return 'conf-tag--warn'
      return 'conf-tag--info'
    },
    isWaiting(registration) {
      return ['VOUCHER_REVIEW', 'INVOICE_REVIEW'].includes(registration.paymentStatus)
    },
    /** 凭证/发票审核中：仍可进入缴费页查看进度与已提交材料 */
    canViewPaymentProgress(registration) {
      if (!registration || (registration.frozenDueToExpiry === '1' && !this.isOfficialCardOnsite(registration))) return false
      return this.isWaiting(registration)
    },
    canResubmitVoucher(registration) {
      if (registration.frozenDueToExpiry === '1' && !this.isOfficialCardOnsite(registration)) return false
      return ['UNPAID', 'VOUCHER_REJECTED'].includes(registration.paymentStatus)
    },
    canUploadInvoice(registration) {
      if (registration.frozenDueToExpiry === '1' && !this.isOfficialCardOnsite(registration)) return false
      return ['INVOICE_PENDING', 'INVOICE_REJECTED', 'INVOICE_OVERDUE'].includes(registration.paymentStatus)
    }
  }
}
</script>

<style scoped>
.conference-list-card {
  gap: 18px;
  overflow: visible;
}

.conf-list-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  position: relative;
  z-index: 5;
}

.conf-list-header h3 {
  margin: 0 0 6px;
  font-size: 22px;
  letter-spacing: 0.02em;
}

.conf-list-subtitle {
  margin: 0;
  color: var(--subtle);
  font-size: 12px;
  line-height: 1.6;
  max-width: 52rem;
}

.conf-branch-filter {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  flex-shrink: 0;
  width: min(280px, 100%);
  z-index: 20;
}

.conf-branch-filter__label {
  color: var(--subtle);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-align: right;
}

.conf-branch-filter__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  min-height: 38px;
  padding: 6px 12px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: #fff;
  color: var(--primary);
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
}

.conf-branch-filter__trigger:hover,
.conf-branch-filter__trigger:focus {
  border-color: rgba(0, 43, 73, 0.45);
  outline: none;
}

.conf-branch-filter__value {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.conf-branch-filter__caret {
  flex-shrink: 0;
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid var(--primary);
}

.conf-branch-filter__menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  left: 0;
  z-index: 30;
  margin: 0;
  padding: 6px;
  list-style: none;
  max-height: min(360px, 60vh);
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 12px 28px rgba(14, 38, 67, 0.16);
  -webkit-overflow-scrolling: touch;
}

.conf-branch-filter__menu.is-up {
  top: auto;
  bottom: calc(100% + 4px);
  box-shadow: 0 -12px 28px rgba(14, 38, 67, 0.16);
}

.conf-branch-filter__menu li {
  margin: 0;
}

.conf-branch-filter__option {
  display: block;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--primary);
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  text-align: left;
  white-space: normal;
  word-break: break-word;
  cursor: pointer;
}

.conf-branch-filter__option:hover {
  background: rgba(0, 43, 73, 0.06);
}

.conf-branch-filter__option.is-active {
  background: rgba(0, 43, 73, 0.12);
  color: var(--primary);
}

.conf-list-empty {
  padding: 48px 24px;
  text-align: center;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: #fff;
  display: grid;
  gap: 10px;
  justify-items: center;
}

.conf-list-empty strong {
  color: var(--primary);
}

.conf-list-empty p {
  margin: 0;
  color: var(--subtle);
  font-size: 13px;
}

.conf-list {
  display: grid;
  gap: 14px;
}

.conf-list-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 168px;
  gap: 20px;
  padding: 22px 22px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 8px 22px rgba(14, 38, 67, 0.05);
  transition: box-shadow 0.18s ease;
}

.conf-list-item:hover {
  box-shadow: 0 12px 28px rgba(14, 38, 67, 0.1);
}

.conf-list-item__main {
  min-width: 0;
  display: grid;
  gap: 10px;
  align-content: start;
}

.conf-list-item__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.conf-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.5;
}

.conf-tag--open {
  background: #f0f9eb;
  color: #3d8b40;
}

.conf-tag--closed {
  background: #f4f4f5;
  color: #6b7280;
}

.conf-tag--branch {
  background: rgba(0, 43, 73, 0.08);
  color: var(--primary);
}

.conf-tag--branch.is-main {
  background: rgba(200, 169, 110, 0.28);
  color: #715a3e;
}

.conf-tag--ok {
  background: #f0f9eb;
  color: #3d8b40;
}

.conf-tag--warn {
  background: #fdf6ec;
  color: #b88230;
}

.conf-tag--danger {
  background: #fef0f0;
  color: #f56c6c;
}

.conf-tag--info {
  background: #ecf5ff;
  color: #1f6feb;
}

.conf-tag--frozen {
  background: #fff7ed;
  color: #c2410c;
}

.conf-list-item__title {
  margin: 0;
  color: var(--primary);
  font-size: 17px;
  font-weight: 800;
  cursor: pointer;
  line-height: 1.45;
}

.conf-list-item__title:hover {
  color: var(--secondary);
}

.conf-list-item__desc {
  margin: 0;
  color: var(--subtle);
  font-size: 12px;
  line-height: 1.7;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.conf-list-item__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  color: var(--subtle);
  font-size: 11px;
}

.conf-list-item__actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  min-width: 0;
}

.conf-list-item__actions .primary-action,
.conf-list-item__actions .ghost-action,
.conf-list-item__actions .gold-action,
.conf-list-item__actions .blue-action,
.conf-list-item__actions .green-action {
  width: 100%;
  justify-content: center;
  text-align: center;
}

.conf-link-action {
  border: 0;
  background: transparent;
  color: var(--secondary);
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  padding: 2px 0;
  text-align: right;
}

.conf-link-action:hover {
  text-decoration: underline;
}

.gold-action {
  border: 0;
  border-radius: 6px;
  background: var(--gold);
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  min-height: 34px;
  cursor: pointer;
}

.gold-action:hover {
  filter: brightness(0.96);
}

.blue-action {
  border: 0;
  border-radius: 6px;
  background: #2563eb;
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  min-height: 34px;
  cursor: pointer;
}

.green-action {
  border: 0;
  border-radius: 6px;
  background: #16a34a;
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  min-height: 34px;
  cursor: pointer;
}

.conf-status-text {
  font-size: 11px;
  font-weight: 700;
  text-align: center;
}

.conf-status-text.is-wait {
  color: #b88230;
}

.conf-status-text.is-closed {
  color: #6b7280;
}

.conf-status-text.is-frozen {
  color: #c2410c;
}

@media (max-width: 860px) {
  .conf-list-header {
    flex-direction: column;
  }

  .conf-branch-filter {
    align-items: stretch;
    width: 100%;
    min-width: 0;
  }

  .conf-list-item {
    grid-template-columns: minmax(0, 1fr);
  }

  .conf-list-item__actions {
    flex-direction: row;
    flex-wrap: wrap;
  }

  .conf-list-item__actions .primary-action,
  .conf-list-item__actions .ghost-action,
  .conf-list-item__actions .gold-action,
  .conf-list-item__actions .blue-action,
  .conf-list-item__actions .green-action {
    width: auto;
    flex: 1 1 auto;
  }

  .conf-link-action {
    text-align: left;
  }
}
</style>
